import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ENV_CONFIG } from 'config/environment';
import { clearLocalStorage, setRoleType } from 'services/storage';
import { useAuth0Config } from 'hooks/useAuth0Config';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { mixpanelClient } from 'helpers/mixpanel';
import { trackEvent } from 'helpers/mixpanelEvents';
import { getActiveRole } from 'selectors/admins';
import { adminsActiveRole } from 'store/admins/slice';
import { adminsApi } from 'services/adminsApi';
import { adminRecipientsApi } from 'services/adminRecipientsApi';
import { setsApi } from 'services/setsApi';
import { baseApi } from 'services/baseApi';
import { nextPayoutAmountApi } from 'services/nextPayoutAmountApi';
import { transactionsDistinctRecipientsApi } from 'services/transactionsDistinctRecipientsApi';
import { eventPagesForReportApi } from 'services/eventPagesForReportApi';
import { impactScoreEligibilityApi } from 'services/impactScoreEligibilityApi';
import { recipientRaceApi } from 'services/recipientRaceApi';
import { shoppingCartApi } from 'services/shoppingCartApi';
export const useNavbarCommon = () => {
  const { logout, isAuthenticated, isLoading } = useAuth0();
  const { handleAccessToken } = useAuth0Config();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeRole = useAppSelector(getActiveRole);

  const isNavOpen = () => document.documentElement.classList.contains('nav-open');

  useEffect(() => {
    if (window.outerWidth < 993 && isNavOpen()) {
      document.documentElement.classList.remove('nav-open');
      setSidebarOpen(false);
    }
  }, [location]);

  // Sync local state with DOM on mount (in case class persisted across navigations)
  useEffect(() => {
    setSidebarOpen(isNavOpen());
  }, []);

  // Sidebar toggle functionality
  const toggleSidebar = () => {
    const willOpen = !isNavOpen();
    if (willOpen) {
      document.documentElement.classList.add('nav-open');
    } else {
      document.documentElement.classList.remove('nav-open');
    }
    setSidebarOpen(willOpen);
  };

  // Logout handler
  const handleLogout = () => {
    trackEvent('Admin Logged Out', {});
    mixpanelClient.reset();
    clearLocalStorage();
    logout({ logoutParams: { returnTo: ENV_CONFIG().SITE_URL } });
  };

  // Dropdown toggle
  const toggle = () => setIsOpen(!isOpen);

  const handleChangeRoleType = roleType => {
    if (roleType !== activeRole) {
      trackEvent('Role Switched', { from_role: activeRole, to_role: roleType });
    }
    dispatch(adminsActiveRole(roleType));
    setRoleType(roleType);
    dispatch(adminsApi.util.resetApiState());
    dispatch(adminRecipientsApi.util.resetApiState());
    dispatch(setsApi.util.resetApiState());
    dispatch(baseApi.util.resetApiState());
    dispatch(nextPayoutAmountApi.util.resetApiState());
    dispatch(transactionsDistinctRecipientsApi.util.resetApiState());
    dispatch(eventPagesForReportApi.util.resetApiState());
    dispatch(impactScoreEligibilityApi.util.resetApiState());
    dispatch(recipientRaceApi.util.resetApiState());
    dispatch(shoppingCartApi.util.resetApiState());
    setIsOpen(false);
    navigate('/dashboard');
  };

  return {
    logout,
    isAuthenticated,
    isLoading,
    handleAccessToken,
    location,
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    handleLogout,
    isOpen,
    setIsOpen,
    toggle,
    handleChangeRoleType,
  };
};
